import { Box, Chip, CircularProgress, Tooltip, useTheme } from '@mui/joy';
import { ChatCircleText, ShareNetwork } from '@phosphor-icons/react';
import { capitalize } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumbs, Button, PageTitle } from 'core/components';
import { AlternateVersionHelper, VersionTypeHelper } from 'core/helpers';
import { IBreadcrumbsItem } from 'core/types';
import { PartDetailsTab } from 'parts/components/PartDetails';
import { QuotePartInlineActionDrawer } from 'quotes/components/QuotePartList';
import { ExternalOrderStatus, OrderStatusTooltip } from 'quotes/elements';
import { QuoteEntityType, QuoteStatus, QuoteStatusLabel, QuoteVersionType } from 'quotes/enums';
import { useCustomerQuotePartsContext } from 'quotes/states';
import {
  ICustomerQuote,
  ICustomerQuotePart,
  IProjectSubmitBlock,
  IProjectSubmitWarning,
  IQuotePart,
  IQuotePendingCliJobs,
} from 'quotes/types';
import { getQuoteVersionNumber } from 'quotes/utils';
import { Color, Size, Variant } from 'theme/enums';

import { useExpiredCostingContext } from './expiredCosting';
import { OrderProjectSubmitBanner } from './OrderProjectSubmitBanner';
import { QuotePartExpiredCostingWarning } from './QuotePartExpiredCostingWarning';
import QuotePartListTable from './QuotePartListTable';
import { QuotePartPricingWindowBanner } from './QuotePartPricingWindowBanner';
import { QuoteVersioningProvider } from './state';
import { isEditable } from './utils';
import { CustomerQuoteDetailsButton } from '../CustomerQuoteDetails';
import { QuotePartAssignVendor } from '../QuotePartAssignVendor';
import { QuotePartDetails } from '../QuotePartDetails';
import QuotePartResetPricingForm from '../QuotePartResetPricingForm';
import QuotePartUpdateMarginForm from '../QuotePartUpdateMarginForm';
import { QuotePartUpdatePnbMarginForm } from '../QuotePartUpdatePnbMarginForm';
import { QuotePartUpsertLine } from '../QuotePartUpsertLine';

interface IProps {
  quote: ICustomerQuote;
  setQuote: (quote: ICustomerQuote) => void;
  pendingCliJobs?: IQuotePendingCliJobs;
  breadcrumbs: IBreadcrumbsItem[];
  activeInlineActionDrawer?: QuotePartInlineActionDrawer;
  onCloseInlineActionDrawer?: () => void;
  defaultQuotePartDetailsTab?: PartDetailsTab;
  isOpenResetPricingForm?: boolean;
  onCloseResetPricingForm?: () => void;
  resetPricingFormPayload?: Record<string, unknown>;
  quoteEntityType?: QuoteEntityType;
  projectSubmitBlock?: IProjectSubmitBlock | null;
  projectSubmitWarning?: IProjectSubmitWarning | null;
}

const CustomerQuotePartListView = ({
  quote,
  setQuote,
  pendingCliJobs = {},
  breadcrumbs,
  activeInlineActionDrawer,
  onCloseInlineActionDrawer,
  defaultQuotePartDetailsTab,
  isOpenResetPricingForm,
  onCloseResetPricingForm,
  resetPricingFormPayload = null,
  quoteEntityType = QuoteEntityType.QUOTE,
  projectSubmitBlock = null,
  projectSubmitWarning = null,
}: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { setDraftVersionChanged, currentQuoteVersion } = useCustomerQuotePartsContext();
  const { expiredCostingAboveThreshold } = useExpiredCostingContext();

  // The grid's own gate for the Edit Line pencil.
  const isLineEditable = useCallback(
    (quotePart: IQuotePart) =>
      isEditable({ data: quotePart as ICustomerQuotePart, currentQuoteVersion }),
    [currentQuoteVersion]
  );

  const buttons = useMemo(
    () => [
      <CustomerQuoteDetailsButton
        key="details"
        quote={quote}
        pendingOrderCliJob={pendingCliJobs.getOrdersInfoFromCore}
        quoteEntityType={quoteEntityType}
        setQuote={setQuote}
      />,
      <Button
        key={'messages'}
        color={Color.PRIMARY}
        variant={Variant.OUTLINED}
        icon={<ChatCircleText />}
        hide
      />,
      <Button
        key={'share'}
        color={Color.PRIMARY}
        variant={Variant.OUTLINED}
        icon={<ShareNetwork />}
        hide
      />,
    ],
    [pendingCliJobs.getOrdersInfoFromCore, quote, quoteEntityType, setQuote]
  );

  const getChips = useCallback(() => {
    const chips = [];

    chips.push(VersionTypeHelper.getVersionTypeChipByQuote(theme, quote, true));

    if (QuoteVersionType.DRAFT === currentQuoteVersion) {
      chips.push({ label: capitalize(currentQuoteVersion) });
    }

    if (
      QuoteEntityType.ORDER === quoteEntityType &&
      [
        QuoteStatus.ORDER_ORDERED,
        QuoteStatus.ORDER_ORDERING,
        QuoteStatus.ORDER_ORDER_FAILED,
        QuoteStatus.ORDER_SUBMISSION_TIMED_OUT,
      ].includes(quote.status)
    ) {
      const colorMap = {
        [QuoteStatus.ORDER_ORDERED]: Color.SUCCESS,
        [QuoteStatus.ORDER_ORDERING]: Color.WARNING,
        [QuoteStatus.ORDER_ORDER_FAILED]: Color.DANGER,
        [QuoteStatus.ORDER_SUBMISSION_TIMED_OUT]: Color.WARNING,
      };

      // Both mean "waiting on the external order system"; only Ordering is actively progressing,
      // so only it carries the spinner.
      const isAwaitingExternalSystem = [
        QuoteStatus.ORDER_ORDERING,
        QuoteStatus.ORDER_SUBMISSION_TIMED_OUT,
      ].includes(quote.status);

      const statusChip = (
        <Chip
          color={colorMap[quote.status]}
          size={Size.LG}
          variant={Variant.SOLID}
          startDecorator={
            quote.status === QuoteStatus.ORDER_ORDERING ? (
              <CircularProgress size={Size.SM} color={Color.WARNING} />
            ) : undefined
          }
          sx={{
            height: 'fit-content',
            ...(isAwaitingExternalSystem && {
              color: theme.palette.warning[700],
            }),
          }}
        >
          {QuoteStatusLabel[quote.status]}
        </Chip>
      );

      if (QuoteStatus.ORDER_SUBMISSION_TIMED_OUT === quote.status) {
        // OrderStatusTooltip's stepper has no timed-out state: it would tick "Order Created".
        chips.push(
          <Tooltip
            key="orderStatusTooltip"
            title={t('tooltipSubmissionTimedOut')}
            placement="bottom"
          >
            {statusChip}
          </Tooltip>
        );
      } else {
        const externalStatusChip =
          QuoteStatus.ORDER_ORDERED === quote.status && quote?.externalStatus ? (
            <ExternalOrderStatus value={quote.externalStatus} size={Size.LG} />
          ) : null;

        chips.push(
          <OrderStatusTooltip key="orderStatusTooltip" quote={quote}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {statusChip}
              {externalStatusChip}
            </Box>
          </OrderStatusTooltip>
        );
      }
    }

    return chips;
  }, [currentQuoteVersion, quote, quoteEntityType, t, theme]);

  const isAlternateVersionEnabled = useMemo(
    () => AlternateVersionHelper.isAlternateVersionEnabled({ quote }),
    [quote]
  );

  const title = useMemo(() => {
    return t('titleCustomerQuote', {
      id: quote?.id,
      version: getQuoteVersionNumber(quote),
      entityType: quoteEntityType,
    });
  }, [quote, quoteEntityType, t]);

  const updateQuote = useCallback(
    (updatedQuote?: ICustomerQuote) => {
      setQuote(updatedQuote);
    },
    [setQuote]
  );

  return (
    <>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <PageTitle title={title} chips={getChips()} buttons={buttons} wrapperStyle={{ pb: '16px' }} />

      <OrderProjectSubmitBanner
        projectSubmitBlock={projectSubmitBlock}
        projectSubmitWarning={projectSubmitWarning}
        quoteEntityType={quoteEntityType}
        quoteStatus={quote.status}
        externalOrderNumber={quote.externalOrderNumber}
      />

      <QuotePartPricingWindowBanner />

      <QuotePartDetails
        quote={quote}
        isOpen={QuotePartInlineActionDrawer.DETAILS === activeInlineActionDrawer}
        onClose={onCloseInlineActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
        defaultTab={defaultQuotePartDetailsTab}
      />

      <QuotePartUpsertLine
        quote={quote}
        isOpen={QuotePartInlineActionDrawer.EDIT_LINE === activeInlineActionDrawer}
        onClose={onCloseInlineActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
        entityType={quoteEntityType}
        isLineEditable={isLineEditable}
      />

      <QuotePartUpdateMarginForm
        isOpen={QuotePartInlineActionDrawer.UPDATE_MARGIN === activeInlineActionDrawer}
        onClose={onCloseInlineActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
        quote={quote}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        interactive
      />

      <QuotePartResetPricingForm
        isOpen={isOpenResetPricingForm}
        onClose={onCloseResetPricingForm}
        onSubmitSuccess={setDraftVersionChanged}
        resetPricingFormPayload={resetPricingFormPayload}
      />

      <QuotePartUpdatePnbMarginForm
        isOpen={QuotePartInlineActionDrawer.UPDATE_PNB_MARGIN === activeInlineActionDrawer}
        onClose={onCloseInlineActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
        interactive
      />

      <QuotePartAssignVendor
        isOpen={QuotePartInlineActionDrawer.ASSIGN_VENDOR === activeInlineActionDrawer}
        onClose={onCloseInlineActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        interactive
      />

      <QuoteVersioningProvider quoteId={quote.id} entityType={quoteEntityType}>
        <QuotePartListTable
          quote={quote}
          updateQuote={updateQuote}
          quoteEntityType={quoteEntityType}
          pendingCliJobs={pendingCliJobs}
          projectSubmitBlock={projectSubmitBlock}
          projectSubmitWarning={projectSubmitWarning}
        />
      </QuoteVersioningProvider>

      {expiredCostingAboveThreshold && (
        <QuotePartExpiredCostingWarning
          quoteId={quote.id}
          quoteEntityType={quoteEntityType}
          quoteVersionType={currentQuoteVersion}
        />
      )}
    </>
  );
};

export default CustomerQuotePartListView;
